import { useCallback, useEffect, useRef } from "react";

/**
 * WebSocket with stable onMessage via ref so parent re-renders do not reconnect.
 * @param {string|null} url
 * @param {(data: Object) => void} onMessage
 * @param {{ onOpen?: (info: { isReconnect: boolean }) => void }} [options]
 * @returns {{ send: (data: Object|string) => void }}
 */
export function useWebSocket(url, onMessage, options = {}) {
  const socketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const onMessageRef = useRef(onMessage);
  const onOpenRef = useRef(options?.onOpen);
  const urlRef = useRef(url);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 1000;

  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    onOpenRef.current = options?.onOpen;
  }, [options?.onOpen]);

  useEffect(() => {
    urlRef.current = url;
  }, [url]);

  useEffect(() => {
    let cancelled = false;
    let hasOpenedOnce = false;

    const clearReconnect = () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };

    const closeSocket = () => {
      const socket = socketRef.current;
      socketRef.current = null;
      if (!socket) return;
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      if (
        socket.readyState === WebSocket.OPEN ||
        socket.readyState === WebSocket.CONNECTING
      ) {
        socket.close(1000, "Component unmounting");
      }
    };

    const connect = () => {
      if (cancelled || !url) return;

      const existing = socketRef.current;
      if (
        existing &&
        (existing.readyState === WebSocket.OPEN ||
          existing.readyState === WebSocket.CONNECTING)
      ) {
        return;
      }

      closeSocket();

      try {
        const socket = new WebSocket(url);
        socketRef.current = socket;

        socket.onopen = () => {
          if (cancelled || urlRef.current !== url) {
            socket.close(1000, "Stale connection");
            return;
          }
          const isReconnect = hasOpenedOnce;
          hasOpenedOnce = true;
          reconnectAttemptsRef.current = 0;
          onOpenRef.current?.({ isReconnect });
        };

        socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            onMessageRef.current?.(data);
          } catch (error) {
            console.error("Error parsing WebSocket message:", error);
          }
        };

        socket.onerror = () => {
          // onclose handles reconnect
        };

        socket.onclose = (event) => {
          if (socketRef.current === socket) {
            socketRef.current = null;
          }
          if (
            cancelled ||
            urlRef.current !== url ||
            event.code === 1000 ||
            event.code === 4401 ||
            event.code === 4403 ||
            reconnectAttemptsRef.current >= maxReconnectAttempts
          ) {
            return;
          }
          const delay =
            reconnectDelay * Math.pow(2, reconnectAttemptsRef.current);
          reconnectAttemptsRef.current += 1;
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        };
      } catch (error) {
        console.error("Error creating WebSocket connection:", error);
      }
    };

    clearReconnect();
    closeSocket();
    reconnectAttemptsRef.current = 0;
    connect();

    return () => {
      cancelled = true;
      clearReconnect();
      closeSocket();
    };
  }, [url]);

  const send = useCallback((data) => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    socket.send(typeof data === "string" ? data : JSON.stringify(data));
  }, []);

  return { send };
}
